import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Plus, Save, Trash2, Eye, FileText, Globe, Lock, ChevronRight, Search, Sparkles } from "lucide-react";
/**
 * BlogManager, admin tab to CRUD blog posts.
 *  - Left: list of all posts (draft + published) with status badge
 *  - Right: form editor (metadata + markdown content) + live preview
 *  - SEO preview: shows what Google would display
 */
export default function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("edit"); // edit | preview | seo
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/blog");
    setPosts(data || []);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return posts
      .filter((p) => !needle || (p.title || "").toLowerCase().includes(needle) || (p.slug || "").toLowerCase().includes(needle))
      .sort((a, b) => (b.published_at || b.created_at || "").localeCompare(a.published_at || a.created_at || ""));
  }, [posts, q]);

  const selected = posts.find((p) => p.id === selectedId);
  useEffect(() => {
    if (selected) setDraft({ ...selected });
    else setDraft(null);
  }, [selectedId]); // eslint-disable-line

  const dirty = useMemo(() => {
    if (!selected || !draft) return false;
    return JSON.stringify(selected) !== JSON.stringify(draft);
  }, [selected, draft]);

  const newPost = () => {
    const tempId = `blog-${Date.now()}`;
    const blank = {
      id: tempId,
      slug: "nieuw-artikel",
      title: "Nieuw artikel",
      excerpt: "",
      content_markdown: "## Begin hier met schrijven\n\nGebruik markdown voor opmaak. Headings (##), lijsten (- item), links ([tekst](url)), etc.",
      status: "draft",
      tags: [],
      language: "nl",
      reading_time_minutes: 5,
      author_name: "Kiosk Configurator Team",
      _new: true,
    };
    setPosts((cur) => [blank, ...cur]);
    setSelectedId(tempId);
    setTab("edit");
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      // Auto-update updated_at
      const payload = { ...draft, updated_at: new Date().toISOString() };
      if (payload.status === "published" && !payload.published_at) {
        payload.published_at = new Date().toISOString();
      }
      if (draft._new) {
        delete payload._new;
        const { data } = await api.post("/admin/blog", payload);
        setPosts((cur) => cur.map((p) => (p.id === draft.id ? data : p)));
        setSelectedId(data.id);
      } else {
        const { data } = await api.put(`/admin/blog/${draft.id}`, payload);
        setPosts((cur) => cur.map((p) => (p.id === data.id ? data : p)));
      }
      toast.success("Artikel opgeslagen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!selected || draft?._new) {
      // Cancel new
      setPosts((cur) => cur.filter((p) => p.id !== selectedId));
      setSelectedId(null);
      return;
    }
    if (!window.confirm(`Artikel "${selected.title}" definitief verwijderen?`)) return;
    try {
      await api.delete(`/admin/blog/${selected.id}`);
      setPosts((cur) => cur.filter((p) => p.id !== selected.id));
      setSelectedId(null);
      toast.success("Verwijderd");
    } catch (e) {
      toast.error("Verwijderen mislukt");
    }
  };

  return (
    <div className="grid grid-cols-12 gap-5" data-testid="blog-manager">
      {/* List */}
      <aside className="col-span-12 lg:col-span-4 xl:col-span-3">
        <div className="card-surface p-4 sticky top-24">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-white">Artikelen</h2>
            <button data-testid="blog-new" onClick={newPost} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] bg-[#D97742] text-white hover:bg-[#c46939]">
              <Plus className="w-3 h-3" /> Nieuw
            </button>
          </div>
          <div className="relative mb-3">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A8B3AD]" />
            <input
              data-testid="blog-search-admin"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Zoek…"
              className="w-full pl-8 pr-2 py-1.5 rounded bg-[#0F1816] border border-[#1F3A2E] text-sm text-white placeholder-[#5d6a64] focus:outline-none focus:border-[#D97742]"
            />
          </div>
          <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
            {filtered.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                data-testid={`blog-row-${p.slug}`}
                className={`w-full text-left px-2.5 py-2 rounded text-sm transition flex items-start gap-2 ${selectedId === p.id ? "bg-[#1F3A2E] text-white" : "text-[#A8B3AD] hover:bg-[#1A2421]"}`}
              >
                <FileText className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold truncate">{p.title}</div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`text-[9px] px-1 rounded ${p.status === "published" ? "bg-[#1F3A2E] text-[#7FD8B8]" : "bg-[#3a1f1f] text-[#D97742]"}`}>{p.status}</span>
                    <span className="text-[9px] text-[#A8B3AD] font-mono truncate">{p.slug}</span>
                  </div>
                </div>
                <ChevronRight className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${selectedId === p.id ? "opacity-100" : "opacity-30"}`} />
              </button>
            ))}
          </div>
        </div>
      </aside>

      {/* Detail */}
      <section className="col-span-12 lg:col-span-8 xl:col-span-9">
        {!draft && (
          <div className="card-surface p-10 text-center text-[#A8B3AD]" data-testid="blog-empty-state">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>Selecteer een artikel of maak een nieuw aan.</p>
          </div>
        )}
        {draft && (
          <div className="space-y-4">
            {/* Toolbar */}
            <div className="card-surface p-3 flex items-center justify-between flex-wrap gap-3">
              <div className="flex gap-1">
                <TabBtn active={tab === "edit"} onClick={() => setTab("edit")}><FileText className="w-3.5 h-3.5" /> Bewerken</TabBtn>
                <TabBtn active={tab === "preview"} onClick={() => setTab("preview")}><Eye className="w-3.5 h-3.5" /> Preview</TabBtn>
                <TabBtn active={tab === "seo"} onClick={() => setTab("seo")}><Sparkles className="w-3.5 h-3.5" /> SEO check</TabBtn>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={del} className="flex items-center gap-1 px-3 py-1.5 rounded text-xs text-[#A8B3AD] hover:text-[#D97742] hover:bg-[#1A2421]" data-testid="blog-delete">
                  <Trash2 className="w-3.5 h-3.5" /> {draft._new ? "Annuleer" : "Verwijder"}
                </button>
                <button
                  onClick={save}
                  disabled={!dirty || saving}
                  data-testid="blog-save"
                  className={`flex items-center gap-1 px-4 py-1.5 rounded text-xs font-semibold transition ${dirty ? "bg-[#D97742] text-white hover:bg-[#c46939]" : "bg-[#1F3A2E] text-[#5d6a64]"}`}
                >
                  <Save className="w-3.5 h-3.5" /> {saving ? "Opslaan…" : dirty ? "Opslaan" : "Opgeslagen"}
                </button>
              </div>
            </div>

            {tab === "edit" && <EditTab draft={draft} setDraft={setDraft} />}
            {tab === "preview" && <PreviewTab post={draft} />}
            {tab === "seo" && <SeoTab post={draft} />}
          </div>
        )}
      </section>
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition ${active ? "bg-[#1F3A2E] text-white" : "text-[#A8B3AD] hover:bg-[#1A2421]"}`}
    >
      {children}
    </button>
  );
}

function EditTab({ draft, setDraft }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const [gen, setGen] = useState(false);
  const [translating, setTranslating] = useState(null); // null | langCode

  const generate = async () => {
    if (!draft.title || draft.title === "Nieuw artikel") {
      toast.error("Vul eerst een titel in");
      return;
    }
    setGen(true);
    try {
      const { data } = await api.post("/admin/blog/generate", {
        title: draft.title,
        sector_slug: draft.sector_slug || "",
        usecase_slug: draft.usecase_slug || "",
        tags: draft.tags || [],
        target_language: draft.language || "nl",
      });
      setDraft((d) => ({
        ...d,
        excerpt: data.excerpt || d.excerpt,
        meta_title: data.meta_title || d.meta_title,
        meta_description: data.meta_description || d.meta_description,
        content_markdown: data.content_markdown || d.content_markdown,
        reading_time_minutes: data.reading_time_minutes || d.reading_time_minutes,
      }));
      toast.success("AI-concept gegenereerd. Pas aan en sla op.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generatie mislukt");
    } finally {
      setGen(false);
    }
  };

  const translateTo = async (targetLang) => {
    setTranslating(targetLang);
    try {
      const { data } = await api.post("/admin/blog/translate", {
        source_post_id: draft.id,
        target_language: targetLang,
      });
      // Create a new draft post in the target language
      const newDraft = {
        ...draft,
        id: `blog-${Date.now()}`,
        slug: `${draft.slug}-${targetLang}`,
        language: targetLang,
        status: "draft",
        published_at: null,
        title: data.title || draft.title,
        excerpt: data.excerpt || "",
        meta_title: data.meta_title || "",
        meta_description: data.meta_description || "",
        content_markdown: data.content_markdown || "",
        _new: true,
      };
      const { data: created } = await api.post("/admin/blog", newDraft);
      toast.success(`Vertaling naar ${targetLang.toUpperCase()} gemaakt als draft`);
      // Reload page to refresh list, simplest UX
      window.location.reload();
      return created;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vertalen mislukt");
    } finally {
      setTranslating(null);
    }
  };

  return (
    <div className="space-y-3">
      {/* AI generator + translate banner */}
      <div className="card-surface p-4 bg-gradient-to-r from-[#1F3A2E]/40 to-[#0F1816] border-[#7FD8B8]/30 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#7FD8B8]/15 border border-[#7FD8B8]/30 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-[#7FD8B8]" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">AI first-draft generator</div>
              <div className="text-[11px] text-[#A8B3AD]">Vul titel + (optioneel) sector/tags. Wij schrijven het concept in jouw taal.</div>
            </div>
          </div>
          <button
            onClick={generate}
            disabled={gen}
            data-testid="blog-ai-generate"
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold transition ${gen ? "bg-[#1F3A2E] text-[#5d6a64] cursor-wait" : "bg-[#7FD8B8] text-[#0a1410] hover:bg-[#6FC9A6]"}`}
          >
            <Sparkles className="w-3.5 h-3.5" /> {gen ? "Genereren…" : "Genereer concept"}
          </button>
        </div>

        {/* Translate row, only visible for saved (non-new) posts */}
        {!draft._new && draft.id && (
          <div className="flex items-center justify-between gap-3 flex-wrap pt-3 border-t border-[#1F3A2E]/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D97742]/15 border border-[#D97742]/30 flex items-center justify-center shrink-0">
                <span className="text-base">🌍</span>
              </div>
              <div>
                <div className="text-sm font-semibold text-white">Vertaal dit artikel</div>
                <div className="text-[11px] text-[#A8B3AD]">Creëert een nieuw draft-artikel met identieke metadata in de gekozen taal.</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {[
                { code: "en", label: "EN 🇬🇧" }, { code: "de", label: "DE 🇩🇪" },
                { code: "fr", label: "FR 🇫🇷" }, { code: "es", label: "ES 🇪🇸" },
                { code: "it", label: "IT 🇮🇹" }, { code: "da", label: "DA 🇩🇰" },
              ].filter((l) => l.code !== draft.language).map((l) => (
                <button
                  key={l.code}
                  onClick={() => translateTo(l.code)}
                  disabled={translating}
                  data-testid={`blog-translate-${l.code}`}
                  className={`px-2.5 py-1 rounded text-[11px] font-semibold border transition ${translating ? "bg-[#1F3A2E] text-[#5d6a64] border-[#1F3A2E]" : "border-[#D97742]/40 text-[#D97742] hover:bg-[#D97742] hover:text-white"}`}
                >
                  {translating === l.code ? "…" : l.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="card-surface p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Titel" required>
          <input data-testid="blog-field-title" value={draft.title || ""} onChange={(e) => set("title", e.target.value)} className={inp} />
        </Field>
        <Field label="Slug (URL)" required>
          <input data-testid="blog-field-slug" value={draft.slug || ""} onChange={(e) => set("slug", e.target.value)} className={`${inp} font-mono`} />
        </Field>
        <Field label="Taal">
          <select value={draft.language || "nl"} onChange={(e) => set("language", e.target.value)} className={inp}>
            <option value="nl">Nederlands</option>
            <option value="en">English</option>
            <option value="de">Deutsch</option>
            <option value="fr">Français</option>
            <option value="es">Español</option>
            <option value="it">Italiano</option>
            <option value="da">Dansk</option>
          </select>
        </Field>
        <Field label="Status">
          <select data-testid="blog-field-status" value={draft.status || "draft"} onChange={(e) => set("status", e.target.value)} className={inp}>
            <option value="draft">Draft (verborgen)</option>
            <option value="published">Gepubliceerd</option>
          </select>
        </Field>
        <Field label="Excerpt (samenvatting)" full>
          <textarea data-testid="blog-field-excerpt" value={draft.excerpt || ""} onChange={(e) => set("excerpt", e.target.value)} rows={2} className={inp} placeholder="Korte intro die ook in de meta description komt (max 160 tekens aanbevolen)" />
          <div className="text-[10px] text-[#A8B3AD] mt-1">{(draft.excerpt || "").length} / 160 tekens (Google knipt af op ~160)</div>
        </Field>
        <Field label="Hero image (URL of pad)">
          <input value={draft.hero_image || ""} onChange={(e) => set("hero_image", e.target.value)} className={inp} placeholder="/images/qiox/clean/QX-21.5-FW.png" />
        </Field>
        <Field label="Leestijd (minuten)">
          <input type="number" min={1} value={draft.reading_time_minutes || 5} onChange={(e) => set("reading_time_minutes", parseInt(e.target.value) || 5)} className={inp} />
        </Field>
        <Field label="Tags (komma-gescheiden)" full>
          <input
            value={(draft.tags || []).join(", ")}
            onChange={(e) => set("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
            placeholder="zwembad, polsbandjes, rfid"
            className={inp}
          />
        </Field>
        <Field label="Sector slug">
          <input value={draft.sector_slug || ""} onChange={(e) => set("sector_slug", e.target.value)} className={inp} placeholder="leisure, hospitality, retail…" />
        </Field>
        <Field label="Use-case slug">
          <input value={draft.usecase_slug || ""} onChange={(e) => set("usecase_slug", e.target.value)} className={inp} placeholder="access-control, ordering-payment…" />
        </Field>
      </div>

      <div className="card-surface p-5">
        <label className="text-xs uppercase tracking-wider text-[#A8B3AD] block mb-2">Inhoud (Markdown)</label>
        <textarea
          data-testid="blog-field-content"
          value={draft.content_markdown || ""}
          onChange={(e) => set("content_markdown", e.target.value)}
          rows={22}
          spellCheck
          className="w-full px-3 py-2 rounded bg-[#0F1816] border border-[#1F3A2E] text-sm text-white font-mono leading-relaxed focus:outline-none focus:border-[#D97742]"
          placeholder="## Heading&#10;&#10;Body text with **bold**, *italics*, [links](https://...) and lists:&#10;- item one&#10;- item two"
        />
        <div className="text-[10px] text-[#A8B3AD] mt-1">{(draft.content_markdown || "").length} tekens · markdown (headings ##, lists -, links [tekst](url), bold **, italic *)</div>
      </div>

      <div className="card-surface p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        <h3 className="md:col-span-2 text-sm font-semibold text-white mb-1">SEO overrides (optioneel)</h3>
        <Field label="Meta title (overrides <title>)">
          <input value={draft.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} className={inp} placeholder={draft.title} />
        </Field>
        <Field label="Meta description (overrides excerpt)">
          <input value={draft.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} className={inp} placeholder={draft.excerpt} />
        </Field>
        <Field label="Canonical URL">
          <input value={draft.canonical_url || ""} onChange={(e) => set("canonical_url", e.target.value)} className={inp} placeholder="laat leeg voor auto" />
        </Field>
        <Field label="OG image (social share image, leeg = hero)">
          <input value={draft.og_image || ""} onChange={(e) => set("og_image", e.target.value)} className={inp} />
        </Field>
      </div>
    </div>
  );
}

const inp = "w-full px-3 py-1.5 rounded bg-[#0F1816] border border-[#1F3A2E] text-sm text-white placeholder-[#5d6a64] focus:outline-none focus:border-[#D97742]";

function Field({ label, children, full, required }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-[11px] uppercase tracking-wider text-[#A8B3AD] block mb-1">{label} {required && <span className="text-[#D97742]">*</span>}</label>
      {children}
    </div>
  );
}

function PreviewTab({ post }) {
  return (
    <div className="card-surface p-6 max-w-3xl mx-auto">
      <div className="text-[11px] text-[#A8B3AD] mb-2">Preview, zo zal je artikel eruitzien op /blog/{post.slug}</div>
      {post.hero_image && (
        <div className="aspect-[16/9] bg-[#0F1816] border border-[#1F3A2E] rounded-xl overflow-hidden mb-4">
          <img src={post.hero_image} alt={post.title} className="w-full h-full object-contain" />
        </div>
      )}
      <h1 className="text-3xl font-semibold text-white leading-tight">{post.title}</h1>
      {post.excerpt && <p className="text-[#A8B3AD] mt-3">{post.excerpt}</p>}
      <hr className="border-[#1F3A2E] my-5" />
      <div className="prose-blog">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content_markdown || ""}</ReactMarkdown>
      </div>
    </div>
  );
}

function SeoTab({ post }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://example.com";
  const url = `${origin}/blog/${post.slug}`;
  const title = post.meta_title || post.title || "(geen titel)";
  const desc = post.meta_description || post.excerpt || "";
  const checks = [
    { ok: title.length >= 30 && title.length <= 70, label: `Title lengte (${title.length} tekens, ideaal 30-70)` },
    { ok: desc.length >= 70 && desc.length <= 160, label: `Description lengte (${desc.length} tekens, ideaal 70-160)` },
    { ok: !!post.hero_image, label: "Hero image aanwezig (voor social previews)" },
    { ok: (post.tags || []).length >= 2, label: `Tags (${(post.tags || []).length}) ideaal 2-6` },
    { ok: !!post.sector_slug, label: "Sector slug ingevuld (verbetert clustering)" },
    { ok: (post.content_markdown || "").length >= 800, label: `Inhoud minimaal 800 tekens (${(post.content_markdown || "").length})` },
    { ok: (post.content_markdown || "").includes("## "), label: "Bevat minimaal één H2 heading (##)" },
    { ok: post.status === "published", label: "Status = published (anders niet vindbaar)" },
  ];
  const score = checks.filter((c) => c.ok).length;
  const total = checks.length;

  return (
    <div className="space-y-4">
      {/* Score */}
      <div className="card-surface p-5">
        <div className="flex items-center gap-3 mb-3">
          <Sparkles className="w-5 h-5 text-[#D97742]" />
          <h3 className="text-base font-semibold text-white">SEO score: {score}/{total}</h3>
          <span className={`text-xs px-2 py-0.5 rounded-full ${score >= 7 ? "bg-[#1F3A2E] text-[#7FD8B8]" : score >= 5 ? "bg-[#3a2d1f] text-[#FFC380]" : "bg-[#3a1f1f] text-[#D97742]"}`}>
            {score >= 7 ? "Top" : score >= 5 ? "OK" : "Verbeteren"}
          </span>
        </div>
        <ul className="space-y-1.5">
          {checks.map((c, i) => (
            <li key={i} className="flex items-center gap-2 text-sm">
              <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${c.ok ? "bg-[#7FD8B8] text-[#0a1410]" : "bg-[#3a1f1f] text-[#D97742]"}`}>
                {c.ok ? "✓" : "!"}
              </span>
              <span className={c.ok ? "text-white" : "text-[#A8B3AD]"}>{c.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Google preview */}
      <div className="card-surface p-5">
        <div className="text-[11px] uppercase tracking-wider text-[#A8B3AD] mb-2 flex items-center gap-1"><Globe className="w-3 h-3" /> Hoe Google dit toont</div>
        <div className="bg-white rounded p-4 max-w-2xl font-arial">
          <div className="text-xs text-[#202124]">{url}</div>
          <div className="text-[20px] text-[#1A0DAB] hover:underline cursor-pointer leading-snug truncate">{title}</div>
          <div className="text-sm text-[#4D5156] leading-snug mt-1">{desc}</div>
        </div>
      </div>

      {/* Social preview */}
      <div className="card-surface p-5">
        <div className="text-[11px] uppercase tracking-wider text-[#A8B3AD] mb-2 flex items-center gap-1"><Globe className="w-3 h-3" /> Social share preview (LinkedIn / X / Facebook)</div>
        <div className="bg-[#0F1816] border border-[#1F3A2E] rounded-lg overflow-hidden max-w-xl">
          {post.hero_image && (
            <div className="aspect-[1.91/1] bg-[#0a1410]">
              <img src={post.og_image || post.hero_image} alt="" className="w-full h-full object-contain" />
            </div>
          )}
          <div className="p-3 border-t border-[#1F3A2E]">
            <div className="text-[10px] uppercase tracking-wider text-[#A8B3AD]">{new URL(url).hostname}</div>
            <div className="text-sm font-semibold text-white mt-1">{title}</div>
            <div className="text-xs text-[#A8B3AD] mt-1 line-clamp-2">{desc}</div>
          </div>
        </div>
      </div>

      {/* JSON-LD inspector */}
      <div className="card-surface p-5">
        <div className="text-[11px] uppercase tracking-wider text-[#A8B3AD] mb-2">JSON-LD Article schema (voor Google rich results)</div>
        <pre className="bg-[#0F1816] border border-[#1F3A2E] rounded p-3 text-[11px] font-mono text-[#7FD8B8] overflow-x-auto">
{JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: post.title,
  description: desc,
  image: post.og_image || post.hero_image,
  datePublished: post.published_at,
  author: { "@type": "Organization", name: post.author_name },
  keywords: (post.tags || []).join(", "),
}, null, 2)}
        </pre>
      </div>
    </div>
  );
}
